import * as z from "zod/mini";
import { devBaseSchema, parseFrameConfig, themeBaseSchema } from "../../lib/parse-frame-config";
import { decodeSignedQuote } from "../../lib/buy/quote";

const publicSchema = z.extend(themeBaseSchema, {
  channelId: z._default(z.string(), ""),
  clientToken: z._default(z.string(), ""),
  // Names must match what the SDK sends.
  signature: z._default(z.string(), ""),
  externalTransactionId: z.optional(z.string()),
  // Read by getPresentationMode(). Listed here so it is not logged as unknown.
  presentation: z.optional(z.string()),
  // Parsed/applied in buttons/apple-pay via parseApplePayButtonSettings; kept as a
  // raw string here so the key is recognized (not warned as unknown).
  applePayButtonSettings: z.optional(z.string()),
  // Parsed/applied in the google-pay and card buttons via ./button-settings; kept as
  // raw strings here so the keys are recognized (not warned as unknown).
  googlePayButtonSettings: z.optional(z.string()),
  cardButtonSettings: z.optional(z.string()),
  // "true" runs the purchase on mount with no button. Cloudflare adds it when it sends Orion's
  // headless /platform/v1/buy here.
  headless: z.optional(z.string()),
  // Ground Control settings. Frames transfers send it as browserInformation.ip.
  clientIp: z.optional(z.string()),
});

const devSchema = z.object({
  ...devBaseSchema.shape,
  /** Dev only. Forwarded as x-mock-scenario. */
  forceError: z._default(z.string(), ""),
  /** Dev only. A local secure wallet, e.g. http://localhost:8082. */
  secureWalletUrl: z.optional(z.string()),
});

const KNOWN_KEYS = new Set([...Object.keys(publicSchema.shape), ...Object.keys(devSchema.shape)]);

export type BuyButtonConfig = z.infer<typeof publicSchema> & z.infer<typeof devSchema>;

export function parseConfig(search?: string): BuyButtonConfig {
  const { config, devConfig } = parseFrameConfig({
    publicSchema,
    devSchema,
    knownKeys: KNOWN_KEYS,
    search,
  });
  return { ...config, ...devConfig };
}

export function validateBuyButtonConfig(config: BuyButtonConfig) {
  if (!config.channelId || !config.clientToken || !config.signature) {
    return {
      success: false,
      code: "configurationError",
      error: "Missing channelId, clientToken, or signature",
    } as const;
  }
  const signedQuote = decodeSignedQuote(config.signature);
  if (!signedQuote) {
    return {
      success: false,
      code: "invalidQuote",
      error: "Could not read the quote signature",
    } as const;
  }
  return { success: true, signedQuote } as const;
}
